import { Controller } from "@hotwired/stimulus"
import {
  buildComparisonCard,
  buildFeeResultRow,
  calculateFeeImpact
} from "../modules/money_smart/kiwi_saver_helpers"

const MAX_COMPARE = 3

export default class extends Controller {
  static targets = [
    "filterControls", "tableBody", "fundCount", "noResults",
    "compareButton", "compareCount", "comparisonPanel", "comparisonCards",
    "feeBalance", "feeYears", "feeResults",
    "typeFilter", "providerFilter", "ethicalFilter", "sortSelect"
  ]

  selectedIds = new Set()

  connect() {
    this.restoreFromUrl()
  }

  filter() {
    const type = this.typeFilterTarget.value
    const provider = this.providerFilterTarget.value
    const ethicalOnly = this.ethicalFilterTarget.checked
    let visible = 0

    this.rows.forEach(row => {
      const matchType = type === "all" || row.dataset.fundType === type
      const matchProvider = provider === "all" || row.dataset.provider === provider
      const matchEthical = !ethicalOnly || row.dataset.ethical === "true"

      if (matchType && matchProvider && matchEthical) {
        row.classList.remove("hidden")
        visible++
      } else {
        row.classList.add("hidden")
      }
    })

    this.fundCountTarget.textContent = visible
    this.noResultsTarget.classList.toggle("hidden", visible > 0)
    this.updateUrl()
  }

  sort() {
    const [key, direction] = this.sortSelectTarget.value.split("-")
    const tbody = this.tableBodyTarget
    const rows = [...this.rows]

    rows.sort((a, b) => {
      const aVal = this.sortValue(a, key)
      const bVal = this.sortValue(b, key)

      if (typeof aVal === "string") {
        return direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }

      if (isNaN(aVal) && isNaN(bVal)) return 0
      if (isNaN(aVal)) return 1
      if (isNaN(bVal)) return -1
      return direction === "asc" ? aVal - bVal : bVal - aVal
    })

    rows.forEach(row => tbody.appendChild(row))
    this.updateUrl()
  }

  toggleCompare(event) {
    const checkbox = event.target
    const id = checkbox.dataset.fundId

    if (checkbox.checked) {
      if (this.selectedIds.size >= MAX_COMPARE) {
        checkbox.checked = false
        return
      }
      this.selectedIds.add(id)
    } else {
      this.selectedIds.delete(id)
    }

    this.compareCountTarget.textContent = this.selectedIds.size
    this.compareButtonTarget.disabled = this.selectedIds.size < 2
    this.updateUrl()
  }

  showComparison() {
    if (this.selectedIds.size < 2) return

    const cards = []
    this.selectedIds.forEach(id => {
      const row = this.tableBodyTarget.querySelector(`tr[data-fund-id="${id}"]`)
      if (row) cards.push(buildComparisonCard(row))
    })

    this.comparisonCardsTarget.innerHTML = cards.join("")
    this.comparisonPanelTarget.classList.remove("hidden")
    this.comparisonPanelTarget.scrollIntoView({ behavior: "smooth", block: "start" })
    this.calculateFeeImpact()
  }

  hideComparison() {
    this.comparisonPanelTarget.classList.add("hidden")
  }

  calculateFeeImpact() {
    if (!this.hasFeeBalanceTarget || !this.hasFeeYearsTarget) return
    const balance = parseFloat(this.feeBalanceTarget.value) || 0
    const years = parseInt(this.feeYearsTarget.value, 10) || 0

    if (balance <= 0 || years <= 0) {
      this.feeResultsTarget.innerHTML = ""
      return
    }

    const results = []
    this.selectedIds.forEach(id => {
      const row = this.tableBodyTarget.querySelector(`tr[data-fund-id="${id}"]`)
      if (!row || row.dataset.fees === "") return

      const feePercent = parseFloat(row.dataset.fees)
      const impact = calculateFeeImpact(balance, years, feePercent)
      if (impact) {
        results.push(buildFeeResultRow(
          row.dataset.providerName, row.dataset.fundName,
          feePercent.toFixed(2), impact
        ))
      }
    })

    this.feeResultsTarget.innerHTML = results.length
      ? results.join("")
      : '<p class="text-stone-400 text-sm">Select funds with fee data to see impact.</p>'
  }

  clearFilters() {
    this.typeFilterTarget.value = "all"
    this.providerFilterTarget.value = "all"
    this.ethicalFilterTarget.checked = false
    this.filter()
  }

  updateUrl() {
    const params = new URLSearchParams()
    if (this.typeFilterTarget.value !== "all") params.set("type", this.typeFilterTarget.value)
    if (this.providerFilterTarget.value !== "all") params.set("provider", this.providerFilterTarget.value)
    if (this.ethicalFilterTarget.checked) params.set("ethical", "1")
    if (this.sortSelectTarget.value !== "name-asc") params.set("sort", this.sortSelectTarget.value)
    if (this.selectedIds.size > 0) params.set("compare", [...this.selectedIds].join(","))

    const url = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname
    window.history.replaceState(null, "", url)
  }

  restoreFromUrl() {
    const params = new URLSearchParams(window.location.search)

    if (params.has("type")) this.typeFilterTarget.value = params.get("type")
    if (params.has("provider")) this.providerFilterTarget.value = params.get("provider")
    if (params.has("ethical")) this.ethicalFilterTarget.checked = true
    if (params.has("sort")) this.sortSelectTarget.value = params.get("sort")

    if (params.has("compare")) {
      params.get("compare").split(",").forEach(id => {
        if (this.selectedIds.size < MAX_COMPARE) {
          const checkbox = this.tableBodyTarget.querySelector(
            `input[data-fund-id="${id}"]`
          )
          if (checkbox) {
            checkbox.checked = true
            this.selectedIds.add(id)
          }
        }
      })
      this.compareCountTarget.textContent = this.selectedIds.size
      this.compareButtonTarget.disabled = this.selectedIds.size < 2
    }

    const hasFilters = params.has("type") || params.has("provider") || params.has("ethical")
    if (hasFilters) this.filter()
    if (params.has("sort")) this.sort()
  }

  sortValue(row, key) {
    switch (key) {
    case "name": return (row.dataset.name || "").toLowerCase()
    case "fees": return parseFloat(row.dataset.fees)
    case "return1yr": return parseFloat(row.dataset.return1yr)
    case "return5yr": return parseFloat(row.dataset.return5yr)
    case "size": return parseFloat(row.dataset.fundSize)
    default: return row.dataset.name || ""
    }
  }

  get rows() {
    return this.tableBodyTarget.querySelectorAll("tr[data-fund-id]")
  }
}
